/**
 * Sentry Error Tracking Configuration
 *
 * Setup:
 * 1. Create a free account at https://sentry.io
 * 2. Create a new React project
 * 3. Copy your DSN
 * 4. Add VITE_SENTRY_DSN to .env.local
 *
 * Environment Variables:
 * - VITE_SENTRY_DSN: Your Sentry DSN (required)
 * - VITE_SENTRY_ENVIRONMENT: Environment name (default: development)
 * - VITE_SENTRY_TRACES_SAMPLE_RATE: Performance monitoring sample rate (default: 0.1 = 10%)
 * - VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: Session replay sample rate (default: 0 = disabled)
 * - VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: Error replay sample rate (default: 1.0 = 100%)
 *
 * Security Note:
 * - This configuration requires 'unsafe-eval' in CSP for Session Replay on errors
 * - replaysSessionSampleRate is set to 0 (disabled) to minimize security risk
 * - replaysOnErrorSampleRate is 100% to capture critical debugging info when errors occur
 * - This is a balanced approach: minimal 'unsafe-eval' usage, maximum debugging capability
 */

import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 *
 * Features enabled:
 * - Error tracking
 * - Performance monitoring (traces)
 * - Session replays (on errors)
 * - Breadcrumbs (user actions)
 * - React error boundary
 *
 * @returns void
 */
export function initSentry(): void {
  // Skip initialization in development if no DSN provided
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('⚠️ Sentry DSN not found. Error tracking disabled.');
    console.warn('Add VITE_SENTRY_DSN to .env.local to enable Sentry.');
    return;
  }

  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ||
                      (import.meta.env.MODE === 'production' ? 'production' : 'development');

  const tracesSampleRate = parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1');
  const replaysSessionSampleRate = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1');
  const replaysOnErrorSampleRate = parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0');

  Sentry.init({
    dsn,
    environment,

    // Performance Monitoring
    tracesSampleRate, // 10% of transactions for performance monitoring
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.vercel\.app/,
      /^https:\/\/advice\.edouard\.ai/,
    ],

    // Session Replay (optimized for security)
    // IMPORTANT: Only capture replays on errors to minimize 'unsafe-eval' usage
    replaysSessionSampleRate: 0, // 0% of normal sessions (disabled for security)
    replaysOnErrorSampleRate, // 100% of sessions with errors (critical for debugging)

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Session replay for debugging
      Sentry.replayIntegration({
        maskAllText: false, // Don't mask text (adjust for privacy if needed)
        blockAllMedia: false, // Don't block images/videos
      }),

      // React-specific error boundary
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
      }),
    ],

    // Release tracking (optional - use with CI/CD)
    release: import.meta.env.VITE_COMMIT_HASH || 'development',

    // Additional options
    beforeSend(event, hint) {
      // Filter out errors you don't want to track
      const error = hint.originalException;

      // Ignore ResizeObserver errors (common, harmless browser quirk)
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message);
        if (message.includes('ResizeObserver')) {
          return null;
        }
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',

      // Network errors (handled by app)
      'NetworkError',
      'Network request failed',

      // Common non-critical errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Set user context (optional - add after authentication)
    // initialScope: {
    //   user: { id: 'user-id', email: 'user@example.com' },
    // },
  });

  console.log(`✅ Sentry initialized (${environment})`);
}

/**
 * Set user context for Sentry
 * Call this after user logs in
 *
 * @param user - User object with id, email, username
 */
export function setSentryUser(user: { id: string; email?: string; username?: string }): void {
  Sentry.setUser(user);
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 * Use this for caught errors you want to track
 *
 * @param error - Error object or string
 * @param context - Additional context
 */
export function captureError(error: Error | string, context?: Record<string, any>): void {
  if (context) {
    Sentry.setContext('additional', context);
  }

  if (typeof error === 'string') {
    Sentry.captureMessage(error, 'error');
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Add a breadcrumb (user action for debugging)
 *
 * @param message - Breadcrumb message
 * @param category - Category (e.g., 'ui', 'navigation', 'http')
 * @param level - Severity level
 */
export function addBreadcrumb(
  message: string,
  category: string = 'user-action',
  level: Sentry.SeverityLevel = 'info'
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Sentry Error Boundary wrapper component
 * Wrap your app with this to catch React errors
 *
 * Example:
 * <SentryErrorBoundary fallback={<ErrorFallback />}>
 *   <App />
 * </SentryErrorBoundary>
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Re-export Sentry for advanced usage
export { Sentry };
